import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import PropertyCard from '../components/PropertyCard';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SavedSearchMatchesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/saved-searches/${id}/matches?limit=50`);
        setData(r.data);
      } catch {
        toast.error('Saved search not found');
        navigate('/dashboard?tab=saved-searches');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user, authLoading, navigate]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-[#E07A3A]" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { search, items } = data;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="saved-search-matches-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/dashboard?tab=saved-searches"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#E07A3A] mb-6"
          data-testid="back-to-saved-searches"
        >
          <ArrowLeft className="h-4 w-4" /> Back to saved searches
        </Link>

        <div className="mb-8">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-2 block uppercase font-medium">Saved search</span>
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A] mb-2">{search.name}</h1>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Bell className="h-4 w-4 text-[#E07A3A]" />
            <span>{items.length} matches · alerts {search.alert_frequency || 'daily'}</span>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-slate-200 p-12 text-center">
            <h3 className="text-xl font-['Playfair_Display'] text-slate-900 mb-2">No matches yet</h3>
            <p className="text-sm text-slate-500">
              We&apos;ll email you as soon as a new property matches this search.
            </p>
          </div>
        ) : (
          <div className="property-grid">
            {items.map((p, i) => (
              <div key={p.id} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.04}s` }}>
                <PropertyCard property={p} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedSearchMatchesPage;
